import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { CustomersService, MemoriamsService, OrganisationDto } from '@careapp-group/ng-sdk';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, concatMap, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { CAAction, Customers, Memoriams } from '../actions';
import { CUSTOMERS, getSelected, getSelectedId, ORGANISATIONS } from '../reducers';

/**
 * Note memoriams are requested by `customerId` but all other actions after successful
 * load operate on the `memoriam.id`
 */
@Injectable()
export class MemoriamEffects {
  getMemoriam$ = createEffect(() =>
    this.actions.pipe(
      ofType(Memoriams.REQUEST_BY_CUSTOMER_ID),
      withLatestFrom(this.store.select(getSelectedId(CUSTOMERS))),
      switchMap(([action, customerId]: [CAAction, string]) =>
        this.customersService.getCustomerMemoriam({ id: customerId }).pipe(
          concatMap((memoriam) => [
            Memoriams.ResponseSuccessByCustomerId({
              id: action.payload.id,
              memoriam,
            }),
            // Implicitly select the memoriam that we get back
            Memoriams.SelectByIdAction({ id: memoriam.id }),
          ]),
          catchError((error) => {
            if (error.statusCode !== 404) {
              return of(
                Memoriams.ResponseFailByCustomerId({
                  id: customerId,
                  error,
                })
              );
            } else {
              return of(
                Memoriams.ResponseSuccessNoMemoriamAction({
                  id: customerId,
                })
              );
            }
          })
        )
      )
    )
  );

  createMemoriam$ = createEffect(() =>
    this.actions.pipe(
      ofType(Memoriams.REQUEST_CREATE),
      withLatestFrom(this.store.select(getSelected(ORGANISATIONS))),
      switchMap(([action, organisation]) =>
        this.memoriamsService
          .createMemoriam({ createMemoriamRequestDto: (<any>action).payload })
          .pipe(
            tap((result) =>
              this.router.navigate([
                '/',
                organisation.tenantId,
                'organisations',
                organisation.id,
                'communities',
                (<any>action).payload.customerId,
                'memoriam',
              ])
            ),
            map((result) => Memoriams.ResponseSuccessCreateAction(result)),
            catchError((error) => of(Memoriams.ResponseFailCreateAction(error)))
          )
      )
    )
  );

  completeMemoriam$ = createEffect(() =>
    this.actions.pipe(
      ofType(Memoriams.REQUEST_COMPLETE_MEMORIAM),
      withLatestFrom(this.store.select(getSelected(ORGANISATIONS))),
      switchMap(([action, organisation]: [CAAction, OrganisationDto]) =>
        this.memoriamsService.completeMemoriam({ id: action.payload.id }).pipe(
          tap((result) =>
            this.router.navigate([
              '/',
              organisation.tenantId,
              'organisations',
              organisation.id,
              'communities',
              result.customerId,
            ])
          ),
          map((result) =>
            Memoriams.ResponseSuccessComplete({
              id: action.payload.id,
            })
          ),
          catchError((error) =>
            of(
              Memoriams.ResponseFailComplete({
                id: action.payload.id,
                error,
              })
            )
          )
        )
      )
    )
  );

  deleteMemoriam$ = createEffect(() =>
    this.actions.pipe(
      ofType(Memoriams.REQUEST_REMOVE_MEMORIAM),
      withLatestFrom(this.store.select(getSelected(ORGANISATIONS))),
      switchMap(([action, organisation]: [CAAction, OrganisationDto]) =>
        this.memoriamsService.deleteMemoriam({ id: action.payload.memoriamId }).pipe(
          tap((result) =>
            this.router.navigate([
              '/',
              organisation.tenantId,
              'organisations',
              organisation.id,
              'communities',
              action.payload.customerId,
            ])
          ),
          map((result) =>
            Memoriams.ResponseSuccessRemove({ memoriamId: action.payload.memoriamId })
          ),
          catchError((error) =>
            of(
              Memoriams.ResponseFailRemove({
                memoriamId: action.payload.memoriamId,
                error,
              })
            )
          )
        )
      )
    )
  );

  requestMemoriamRelatedData$ = createEffect(() =>
    this.actions.pipe(
      ofType(Memoriams.RESPONSE_SUCCESS_BY_ID),
      map((action: any) => Customers.RequestByIdAction({ id: action.payload.customerId }))
    )
  );

  constructor(
    private actions: Actions,
    private store: Store<any>,
    private router: Router,
    private memoriamsService: MemoriamsService,
    private customersService: CustomersService
  ) {}
}
